/**
 * jhc-product.js
 * - Image gallery (thumbnails)
 * - Variant selection + price/URL/image update
 * - Quantity input
 * - Accordion (default open state supported via .is-open on load)
 * - Add to cart with loading state
 * - Overview tabs (desktop tab switch / mobile single-open accordion)
 * - Overview quick link (Recipe -> Brewing Guide)
 * - Related products via Shopify recommendations endpoint
 */

(() => {
  'use strict';

  // ---------------------------------------------------------------------
  // GALLERY
  // ---------------------------------------------------------------------

  function initGallery() {
    const mainImage = document.querySelector('.jhc-product__featured-image');
    const thumbnails = Array.from(
      document.querySelectorAll('.jhc-product__thumbnail')
    );

    if (!mainImage || !thumbnails.length) return;

    function setActive(index) {
      const thumb = thumbnails[index];
      if (!thumb) return;

      const img = thumb.querySelector('img');
      if (!img) return;

      mainImage.src = thumb.dataset.imageSrc;
      mainImage.alt = img.alt || mainImage.alt;

      // Prevent browser from continuing to use thumbnail srcset.
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');

      thumbnails.forEach((item) => {
        item.classList.remove('is-active');
      });

      thumb.classList.add('is-active');
    }

    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        setActive(index);
      });
    });
  }

  // ---------------------------------------------------------------------
  // VARIANTS
  // ---------------------------------------------------------------------

  function initVariants() {
    const form = document.getElementById('jhc-product-form');
    const variantInput = document.getElementById('jhc-variant-id');
    const priceEl = document.getElementById('jhc-product-price');
    const addBtn = document.querySelector('[data-add-to-cart]');

    const pills = document.querySelectorAll(
      '.jhc-product__pill-input'
    );

    const selectedLabels = document.querySelectorAll(
      '[data-option-index]'
    );

    if (!form || !variantInput) return;

    const productData = window.__jhcProductData;

    if (!productData) return;

    function getSelectedOptions() {
      const options = {};

      pills.forEach((pill) => {
        if (pill.checked) {
          options[pill.dataset.optionName] =
            pill.dataset.optionValue;
        }
      });

      return options;
    }

    function findVariant(selectedOptions) {
      return productData.variants.find((variant) =>
        variant.options.every((optionValue, index) => {
          const optionName = productData.options[index];

          return (
            selectedOptions[optionName] === optionValue
          );
        })
      );
    }

    function updatePrice(variant) {
      if (!priceEl || !variant) return;

      const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: productData.currency || 'USD',
      });

      if (
        variant.compare_at_price &&
        variant.compare_at_price > variant.price
      ) {
        priceEl.innerHTML = `
          <span class="jhc-product__price-sale">
            ${formatter.format(variant.price / 100)}
          </span>

          <span class="jhc-product__price-compare">
            ${formatter.format(variant.compare_at_price / 100)}
          </span>
        `;
      } else {
        priceEl.innerHTML = `
          <span class="jhc-product__price-regular">
            ${formatter.format(variant.price / 100)}
          </span>
        `;
      }
    }

    function updateButton(variant) {
      if (!addBtn) return;

      if (variant?.available) {
        addBtn.disabled = false;

        addBtn.textContent =
          window.variantStrings?.addToCart ||
          'Add to cart';
      } else {
        addBtn.disabled = true;

        addBtn.textContent =
          window.variantStrings?.soldOut ||
          'Sold out';
      }
    }

    function updateURL(variant) {
      if (!variant) return;

      const url = new URL(window.location.href);

      url.searchParams.set(
        'variant',
        variant.id
      );

      window.history.replaceState(
        {},
        '',
        url.toString()
      );
    }

    function updateFeaturedImage(variant) {
      if (!variant?.featured_image?.src) return;

      const mainImage = document.querySelector(
        '.jhc-product__featured-image'
      );

      if (!mainImage) return;

      const imageUrl = new URL(
        variant.featured_image.src,
        window.location.origin
      );

      imageUrl.searchParams.set(
        'width',
        '3000'
      );

      mainImage.src = imageUrl.toString();

      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
    }

    function updateSelectedLabels() {
      selectedLabels.forEach((label) => {
        const index = Number.parseInt(
          label.dataset.optionIndex,
          10
        );

        const group =
          document.querySelectorAll(
            '.jhc-product__option'
          )[index];

        const checked = group?.querySelector(
          '.jhc-product__pill-input:checked'
        );

        if (checked) {
          label.textContent =
            checked.dataset.optionValue;
        }
      });
    }

    pills.forEach((pill) => {
      pill.addEventListener('change', () => {
        const selectedOptions =
          getSelectedOptions();

        const variant =
          findVariant(selectedOptions);

        updateSelectedLabels();

        if (!variant) {
          updateButton(null);
          return;
        }

        variantInput.value = variant.id;

        updatePrice(variant);
        updateButton(variant);
        updateURL(variant);
        updateFeaturedImage(variant);
      });
    });
  }

  // ---------------------------------------------------------------------
  // QUANTITY
  // ---------------------------------------------------------------------

  function initQuantity() {
    const minusBtn = document.querySelector(
      '[data-quantity-minus]'
    );

    const plusBtn = document.querySelector(
      '[data-quantity-plus]'
    );

    const input = document.querySelector(
      '[data-quantity-input]'
    );

    if (!input) return;

    minusBtn?.addEventListener('click', () => {
      const value =
        Number.parseInt(input.value, 10) || 1;

      input.value = Math.max(
        1,
        value - 1
      );
    });

    plusBtn?.addEventListener('click', () => {
      const value =
        Number.parseInt(input.value, 10) || 1;

      input.value = value + 1;
    });
  }

  // ---------------------------------------------------------------------
  // ACCORDION — MOBILE DESCRIPTION FALLBACK
  // ---------------------------------------------------------------------

  function initAccordions() {
    const accordions =
      document.querySelectorAll(
        '[data-accordion]'
      );

    if (!accordions.length) return;

    accordions.forEach((accordion) => {
      const trigger =
        accordion.querySelector(
          '[data-accordion-trigger]'
        );

      if (!trigger) return;

      trigger.addEventListener('click', () => {
        const isOpen =
          accordion.classList.contains(
            'is-open'
          );

        accordions.forEach((item) => {
          item.classList.remove('is-open');

          item
            .querySelector(
              '[data-accordion-trigger]'
            )
            ?.setAttribute(
              'aria-expanded',
              'false'
            );
        });

        if (!isOpen) {
          accordion.classList.add(
            'is-open'
          );

          trigger.setAttribute(
            'aria-expanded',
            'true'
          );
        }
      });
    });
  }

  // ---------------------------------------------------------------------
  // OVERVIEW TABS
  //
  // Desktop:
  // Sidebar links switch visible panels.
  //
  // Mobile:
  // Panel headings work as a single-open accordion.
  //
  // Recipe quick link:
  // Activates Brewing Guide.
  // On mobile it also scrolls to the opened Brewing Guide panel.
  // ---------------------------------------------------------------------

  function initOverview() {
    const root = document.querySelector(
      '[data-overview]'
    );

    if (!root) return;

    const navLinks = Array.from(
      root.querySelectorAll(
        '[data-overview-nav-link]'
      )
    );

    const panels = Array.from(
      root.querySelectorAll(
        '[data-overview-panel]'
      )
    );

    const quickLinks = Array.from(
      root.querySelectorAll(
        '[data-overview-quick-link]'
      )
    );

    function activatePanel(targetId) {
      panels.forEach((panel) => {
        const isTarget =
          panel.id === targetId;

        panel.classList.toggle(
          'is-active',
          isTarget
        );

        const trigger =
          panel.querySelector(
            '[data-overview-accordion-trigger]'
          );

        trigger?.setAttribute(
          'aria-expanded',
          String(isTarget)
        );
      });

      navLinks.forEach((link) => {
        link.classList.toggle(
          'is-active',
          link.dataset.target === targetId
        );
      });
    }

    // Desktop navigation
    navLinks.forEach((link) => {
      link.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          activatePanel(
            link.dataset.target
          );
        }
      );
    });

    // Mobile accordion headings
    panels.forEach((panel) => {
      const trigger =
        panel.querySelector(
          '[data-overview-accordion-trigger]'
        );

      trigger?.addEventListener(
        'click',
        () => {
          const isOpen =
            panel.classList.contains(
              'is-active'
            );

          activatePanel(
            isOpen
              ? null
              : panel.id
          );
        }
      );
    });

    // Recipe -> Brewing Guide
    quickLinks.forEach((link) => {
      link.addEventListener(
        'click',
        (event) => {
          const targetId =
            link.dataset.target;

          const target = targetId
            ? document.getElementById(
                targetId
              )
            : null;

          if (!targetId || !target) {
            return;
          }

          event.preventDefault();

          activatePanel(targetId);

          // Only scroll on mobile.
          if (
            window.matchMedia(
              '(max-width: 991px)'
            ).matches
          ) {
            const reduceMotion =
              window.matchMedia(
                '(prefers-reduced-motion: reduce)'
              ).matches;

            target.scrollIntoView({
              behavior: reduceMotion
                ? 'auto'
                : 'smooth',

              block: 'start',
            });
          }
        }
      );
    });
  }

  // ---------------------------------------------------------------------
  // RELATED PRODUCTS
  // Shopify recommendations endpoint
  // ---------------------------------------------------------------------

  function initRecommendations() {
    const section =
      document.querySelector(
        '[data-recommendations]'
      );

    if (!section) return;

    const url = section.dataset.url;

    const grid =
      section.querySelector(
        '[data-recommendations-grid]'
      );

    if (!url || !grid) return;

    fetch(url)
      .then((response) =>
        response.ok
          ? response.text()
          : Promise.reject(
              response.status
            )
      )

      .then((text) => {
        const doc =
          new DOMParser().parseFromString(
            text,
            'text/html'
          );

        const freshGrid =
          doc.querySelector(
            '[data-recommendations-grid]'
          );

        if (
          freshGrid?.children.length
        ) {
          grid.innerHTML =
            freshGrid.innerHTML;
        } else {
          section.remove();
        }
      })

      .catch((error) => {
        console.error(
          '[JHC Product] recommendations fetch failed',
          error
        );

        section.remove();
      });
  }

  // ---------------------------------------------------------------------
  // ADD TO CART
  // ---------------------------------------------------------------------

  function initAddToCart() {
    const form =
      document.getElementById(
        'jhc-product-form'
      );

    const addBtn =
      document.querySelector(
        '[data-add-to-cart]'
      );

    if (!form || !addBtn) return;

    form.addEventListener(
      'submit',
      async (event) => {
        event.preventDefault();

        const variantId =
          document.getElementById(
            'jhc-variant-id'
          )?.value;

        const quantity =
          document.querySelector(
            '[data-quantity-input]'
          )?.value || 1;

        if (!variantId) return;

        addBtn.classList.add(
          'is-loading'
        );

        addBtn.setAttribute(
          'aria-busy',
          'true'
        );

        addBtn.disabled = true;

        try {
          const response =
            await fetch(
              '/cart/add.js',
              {
                method: 'POST',

                headers: {
                  'Content-Type':
                    'application/json',

                  Accept:
                    'application/json',
                },

                body: JSON.stringify({
                  items: [
                    {
                      id:
                        Number.parseInt(
                          variantId,
                          10
                        ),

                      quantity:
                        Math.max(
                          1,
                          Number.parseInt(
                            quantity,
                            10
                          ) || 1
                        ),
                    },
                  ],
                }),
              }
            );

          if (!response.ok) {
            throw new Error(
              'Add to cart failed'
            );
          }

          await response.json();

          const cartResponse =
            await fetch(
              '/cart.js',
              {
                headers: {
                  Accept:
                    'application/json',
                },
              }
            );

          if (!cartResponse.ok) {
            throw new Error(
              'Cart refresh failed'
            );
          }

          const cart =
            await cartResponse.json();

          document.dispatchEvent(
            new CustomEvent(
              'jhc:cart:updated',
              {
                detail: {
                  count:
                    cart.item_count,

                  cart,
                },
              }
            )
          );

          addBtn.classList.remove(
            'is-loading'
          );

          addBtn.classList.add(
            'is-success'
          );

          addBtn.textContent =
            '✓ Added to cart';

          addBtn.setAttribute(
            'aria-busy',
            'false'
          );

          window.setTimeout(() => {
            addBtn.classList.remove(
              'is-success'
            );

            addBtn.textContent =
              window.variantStrings
                ?.addToCart ||
              'Add to cart';

            addBtn.disabled = false;
          }, 2500);
        } catch (error) {
          console.error(
            '[JHC Product]',
            error
          );

          addBtn.classList.remove(
            'is-loading'
          );

          addBtn.textContent =
            'Error — try again';

          addBtn.setAttribute(
            'aria-busy',
            'false'
          );

          window.setTimeout(() => {
            addBtn.textContent =
              window.variantStrings
                ?.addToCart ||
              'Add to cart';

            addBtn.disabled = false;
          }, 2000);
        }
      }
    );
  }

  // ---------------------------------------------------------------------
  // INIT
  // ---------------------------------------------------------------------

  function init() {
    initGallery();
    initVariants();
    initQuantity();
    initAccordions();
    initAddToCart();
    initOverview();
    initRecommendations();
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      {
        once: true,
      }
    );
  } else {
    init();
  }
})();